import { DataStore, SortDirection } from "aws-amplify";
import { useDispatch, useSelector } from "react-redux";
import { setListing, setSelected } from "../store/ducks/media";
import { HeadCell } from "../models/dataTable";
import useApp from "./useApp";
import { Media } from "../models";
import { CreateMediaInput } from "../models/api";
import {
  CreateVariables,
  MediaBulkTrashVariables,
  GetVariables,
  UpdateVariables,
  Option,
  ListingByAccountVariables,
} from "../models/app";

const useResource = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { showConfirm, showError } = useApp();
  const accountsSelected = useSelector((state: any) => state.accounts.selected);

  async function fetch(params: ListingByAccountVariables) {
    const { accountID, searchText, startIndex, limit } = params;

    try {
      const listing = await DataStore.query(
        Media as any,
        (model: any) => {
          model.deleted("eq", "0").accountID("eq", accountID);

          if (searchText.length > 0)
            model.filename("contains", searchText.toLowerCase());

          return model;
        },
        {
          page: startIndex / limit,
          limit: limit,
          sort: (s) => s.createdAt(SortDirection.DESCENDING),
        }
      );

      return listing;
    } catch (err) {
      showError(err);
    }
  }

  async function get(params: GetVariables) {
    const { id } = params;

    try {
      const single: Media | undefined = await DataStore.query(Media as any, id);

      return single;
    } catch (err) {
      showError(err);
    }
  }

  async function create(params: CreateVariables) {
    const { userID, userName, data } = params;

    try {
      const createInput: CreateMediaInput = {
        accountID: accountsSelected.id,
        fileUrl: data.fileUrl,
        filename: data.filename.toLowerCase(),
        filetype: data.filetype,
        fileSize: data.fileSize,
        alternativeText: data.alternativeText,
        caption: data.caption,
        description: data.description,
        attachments: data.attachments,
        deleted: "0",
        createdAt: new Date().toLocaleString(),
        createdByID: userID,
        createdByName: userName,
      };

      await DataStore.save(new Media(createInput as any));

      showConfirm(`New ${singleName} has been created successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function update(params: UpdateVariables) {
    const { id, data } = params;

    try {
      const original = await get({ id });

      await DataStore.save(
        Media.copyOf(original!, (updated) => {
          updated.fileUrl =
            data.fileUrl !== undefined
              ? data.fileUrl.toLowerCase()
              : original!.fileUrl;
          updated.filename =
            data.filename !== undefined
              ? data.filename.toLowerCase()
              : original!.filename;
          updated.filetype =
            data.filetype !== undefined
              ? data.filetype.toLowerCase()
              : original!.filetype;
          updated.fileSize =
            data.fileSize !== undefined ? data.fileSize : original!.fileSize;
          updated.alternativeText =
            data.alternativeText !== undefined
              ? data.alternativeText.toLowerCase()
              : original!.alternativeText;
          updated.caption =
            data.caption !== undefined
              ? data.caption.toLowerCase()
              : original!.caption;
          updated.description =
            data.description !== undefined
              ? data.description.toLowerCase()
              : original!.description;
          updated.attachments =
            data.attachments !== undefined
              ? data.attachments.toLowerCase()
              : original!.attachments;
        })
      );

      showConfirm(`${singleName} has been updated successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function trash(params: GetVariables) {
    try {
      const original = await get(params);

      await DataStore.save(
        Media.copyOf(original!, (updated) => {
          updated.deleted = "1";
        })
      );

      showConfirm(`${singleName} has been moved to trash successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function bulkTrash(params: MediaBulkTrashVariables) {
    const { ids, listing } = params;

    ids.forEach(async (id: any) => {
      try {
        await trash(id);
      } catch (err: Error | any) {
        throw err;
      }
    });

    dispatch(setListing(listing.filter((model: any) => !ids.has(model.id))));

    showConfirm(`${ids.size} ${listingName} items has been moved to trash`);
  }

  async function remove(params: GetVariables) {
    const { id } = params;

    try {
      await DataStore.delete(id as any);

      // dispatch(setListing(listing.filter((model: any) => model.id !== id)));

      showConfirm(`${singleName} has been deleted successfully`);
    } catch (err: Error | any) {
      showError(err);
    }
  }

  function options(listing: Media[]) {
    const options: Option[] = [];

    for (let option of listing) {
      options.push({ label: option.filename, value: option.id });
    }

    return options;
  }

  const headCells: readonly HeadCell[] = [
    {
      id: "name",
      numeric: false,
      disablePadding: false,
      label: "Name",
    },
    {
      id: "createdBy",
      numeric: false,
      disablePadding: false,
      label: "Created By",
    },
    {
      id: "createdAt",
      numeric: false,
      disablePadding: false,
      label: "Date",
    },
    {
      id: "actions",
      numeric: true,
      disablePadding: false,
      label: "",
    },
  ];

  const dataCells: readonly string[] = ["name"];

  const api: any = {};

  api[`${listingName}Model`] = Media as any;
  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}Options`] = options;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}Get`] = get;
  api[`${listingName}Create`] = create;
  api[`${listingName}Update`] = update;
  api[`${listingName}Trash`] = trash;
  api[`${listingName}BulkTrash`] = bulkTrash;
  api[`${listingName}Delete`] = remove;
  api[`${listingName}ChangeListing`] = (listing: Media[]) =>
    dispatch(setListing(listing));
  api[`${listingName}ChangeSelected`] = (id: string) =>
    dispatch(setSelected(id));

  return api;
};

export default useResource;
